import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createAgent,
  createFeature,
  deleteAgent,
  deleteFeature,
  fetchAgents,
  fetchFeatures,
  Model,
  ModelCreate,
  ModelUpdate,
  modelsAPI,
  toggleAgentActive,
  toggleAgentImplicit,
  toggleFeatureActive,
  toggleFeatureImplicit,
  updateAgent,
  updateFeature,
} from '@/api/models';
import { queryKeys } from '@/lib/queryKeys';

export interface AdminAgent {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  is_implicit?: boolean;
  is_active?: boolean;
}

export interface AdminFeature {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  is_implicit?: boolean;
  is_active?: boolean;
}

function normalizeModels(payload: unknown): Model[] {
  if (Array.isArray(payload)) {
    return payload as Model[];
  }

  const candidate = payload as { models?: unknown; items?: unknown; data?: unknown };
  if (Array.isArray(candidate?.models)) {
    return candidate.models as Model[];
  }

  if (Array.isArray(candidate?.items)) {
    return candidate.items as Model[];
  }

  if (Array.isArray(candidate?.data)) {
    return candidate.data as Model[];
  }

  return [];
}

function normalizeAgents(payload: unknown): AdminAgent[] {
  const list = (payload as any[]) || [];
  return list.map((agent: any) => ({
    id: agent.id,
    name: agent.name || agent.id,
    description: agent.description,
    icon: agent.icon,
    is_implicit: !!agent.is_implicit,
    is_active: agent.is_active !== undefined ? agent.is_active : true,
  }));
}

function normalizeFeatures(payload: unknown): AdminFeature[] {
  const list = (payload as any[]) || [];
  return list.map((feature: any) => ({
    id: feature.id,
    name: feature.name || feature.id,
    description: feature.description,
    icon: feature.icon,
    is_implicit: !!feature.is_implicit,
    is_active: feature.is_active !== undefined ? feature.is_active : true,
  }));
}

export function useAdminModelsQuery(enabled: boolean = true) {
  return useQuery({
    queryKey: queryKeys.admin.models.list(),
    enabled,
    queryFn: async () => {
      const response = await modelsAPI.getModels({ per_page: 100 });
      return normalizeModels(response);
    },
  });
}

export function useCreateModelMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ModelCreate) => modelsAPI.createModel(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.admin.models.all });
    },
  });
}

export function useUpdateModelMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ modelId, payload }: { modelId: string; payload: ModelUpdate }) =>
      modelsAPI.updateModel(modelId, payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.admin.models.all });
    },
  });
}

export function useDeleteModelMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (modelId: string) => modelsAPI.deleteModel(modelId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.admin.models.all });
    },
  });
}

export function useToggleModelActiveMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (modelId: string) => modelsAPI.toggleModelActive(modelId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.admin.models.all });
    },
  });
}

export function useToggleModelImplicitMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (modelId: string) => modelsAPI.toggleModelImplicit(modelId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.admin.models.all });
    },
  });
}

export function useAdminAgentsQuery(enabled: boolean = true) {
  return useQuery({
    queryKey: queryKeys.admin.agents.list(),
    enabled,
    queryFn: async () => {
      const response = await fetchAgents();
      return normalizeAgents(response);
    },
  });
}

export function useCreateAgentMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: any) => createAgent(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.admin.agents.all });
    },
  });
}

export function useUpdateAgentMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ agentId, payload }: { agentId: string; payload: any }) =>
      updateAgent(agentId, payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.admin.agents.all });
    },
  });
}

export function useDeleteAgentMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (agentId: string) => deleteAgent(agentId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.admin.agents.all });
    },
  });
}

export function useToggleAgentActiveMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (agentId: string) => toggleAgentActive(agentId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.admin.agents.all });
    },
  });
}

export function useToggleAgentImplicitMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (agentId: string) => toggleAgentImplicit(agentId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.admin.agents.all });
    },
  });
}

export function useAdminFeaturesQuery(enabled: boolean = true) {
  return useQuery({
    queryKey: queryKeys.admin.features.list(),
    enabled,
    queryFn: async () => {
      const response = await fetchFeatures();
      return normalizeFeatures(response);
    },
  });
}

export function useCreateFeatureMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: any) => createFeature(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.admin.features.all });
    },
  });
}

export function useUpdateFeatureMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ featureId, payload }: { featureId: string; payload: any }) =>
      updateFeature(featureId, payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.admin.features.all });
    },
  });
}

export function useDeleteFeatureMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (featureId: string) => deleteFeature(featureId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.admin.features.all });
    },
  });
}

export function useToggleFeatureActiveMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (featureId: string) => toggleFeatureActive(featureId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.admin.features.all });
    },
  });
}

export function useToggleFeatureImplicitMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (featureId: string) => toggleFeatureImplicit(featureId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.admin.features.all });
    },
  });
}
